import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { apiGet } from "../lib/api";
import { formatDate } from "../lib/format";
import { useDocumentMeta } from "../lib/meta";
import type { DonationStatusPayload } from "../lib/types";
import { ButtonLink } from "../components/ui/Button";
import { LoadingBlock } from "../components/ui/LoadingBlock";
import { PageIntro } from "../components/ui/PageIntro";
import { StatusBanner } from "../components/ui/StatusBanner";

type LookupState =
  | { phase: "idle" | "loading" }
  | { phase: "success"; payload: DonationStatusPayload }
  | { phase: "error"; message: string };

export const ThankYouPage = () => {
  const [searchParams] = useSearchParams();
  const [state, setState] = useState<LookupState>({ phase: "idle" });
  const provider = searchParams.get("provider");
  const sessionId = searchParams.get("session_id");
  const orderId = searchParams.get("order_id");

  useDocumentMeta({
    title: "Thank You",
    description: "Donation confirmation and verification lookup for The Education Project.",
  });

  let lookupPath: string | null = null;

  if (provider === "stripe" && sessionId) {
    lookupPath = `/api/donations/status?provider=stripe&session_id=${encodeURIComponent(sessionId)}`;
  } else if (provider === "paypal" && orderId) {
    lookupPath = `/api/donations/status?provider=paypal&order_id=${encodeURIComponent(orderId)}`;
  }

  useEffect(() => {
    if (!lookupPath) {
      setState({ phase: "error", message: "A verified donation lookup link was not found." });
      return;
    }

    let attempts = 0;
    let timeoutId: number | null = null;
    let cancelled = false;

    const poll = async () => {
      setState({ phase: "loading" });

      try {
        const response = await apiGet<{ ok: true; data: DonationStatusPayload }>(lookupPath);

        if (cancelled) {
          return;
        }

        setState({ phase: "success", payload: response.data });

        if (response.data.status === "pending" && attempts < 6) {
          attempts += 1;
          timeoutId = window.setTimeout(poll, 3000);
        }
      } catch (error) {
        if (cancelled) {
          return;
        }

        setState({
          phase: "error",
          message: error instanceof Error ? error.message : "Donation confirmation could not be loaded.",
        });
      }
    };

    poll();

    return () => {
      cancelled = true;
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [lookupPath]);

  return (
    <div className="flex flex-col bg-white">
      {/* Full-bleed Photo Hero Banner */}
      <PageIntro
        eyebrow="Thank you"
        title="Your support matters."
        summary="This page checks the trusted server-side confirmation state of your donation and shows the verification number only after payment has been confirmed."
      />

      {/* Section 1: Confirmation Status - White Background */}
      <section className="bg-white py-16 md:py-24">
        <div className="mx-auto max-w-2xl px-6">
          {state.phase === "loading" || state.phase === "idle" ? (
            <LoadingBlock
              title="Checking donation status"
              message="Please wait while the site verifies the trusted confirmation state of your donation."
            />
          ) : null}

          {state.phase === "error" ? (
            <div className="space-y-6">
              <StatusBanner tone="error" title="We could not verify the donation yet" message={state.message} />
              <div className="flex flex-col gap-3 sm:flex-row justify-center">
                <ButtonLink to="/" variant="primary" className="uppercase tracking-wider text-sm !px-6">Return home</ButtonLink>
                <ButtonLink to="/contact" variant="lime" className="uppercase tracking-wider text-sm !px-6">
                  Contact the organization
                </ButtonLink>
              </div>
            </div>
          ) : null}

          {state.phase === "success" ? (
            <div className="space-y-10">
              {state.payload.status === "confirmed" ? (
                <>
                  <StatusBanner
                    tone="success"
                    title="Donation verified"
                    message="A confirmation email and internal notification have been triggered through the server-side workflow."
                  />
                  <div className="grid gap-8 md:grid-cols-2 border-t border-stone-200 pt-8 mt-8">
                    <div className="space-y-2">
                      <p className="text-xs uppercase font-bold tracking-widest text-[#0293cc]">Verification number</p>
                      <h2 className="text-xl font-display font-bold text-[#0E1C2C]">{state.payload.referenceNumber}</h2>
                      <p className="text-sm text-stone-600">Keep this number with your records.</p>
                    </div>
                    <div className="space-y-2 md:border-l md:border-stone-200 md:pl-8">
                      <p className="text-xs uppercase font-bold tracking-widest text-[#CEDC00]">Confirmed donation</p>
                      <h2 className="text-xl font-display font-bold text-[#0E1C2C]">{state.payload.amountFormatted}</h2>
                      <p className="text-sm text-stone-600">
                        {state.payload.confirmedAt ? `Confirmed ${formatDate(state.payload.confirmedAt)}.` : "Confirmed."}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col gap-3 sm:flex-row justify-center pt-8 border-t border-stone-200 mt-8">
                    <ButtonLink to="/" variant="primary" className="uppercase tracking-wider text-sm !px-6">Return home</ButtonLink>
                    <ButtonLink to="/about" variant="lime" className="uppercase tracking-wider text-sm !px-6">
                      Learn more about the organization
                    </ButtonLink>
                  </div>
                </>
              ) : null}

              {state.payload.status === "pending" ? (
                <StatusBanner
                  tone="neutral"
                  title="Processing your donation"
                  message="Your payment provider has redirected you back to the site, but the verified server-side confirmation is still processing. This page will continue checking automatically."
                />
              ) : null}

              {state.payload.status === "failed" || state.payload.status === "unknown" ? (
                <>
                  <StatusBanner
                    tone="error"
                    title="Donation not confirmed"
                    message="The website could not verify a completed donation from this return link. You can try again or contact the organization for help."
                  />
                  <div className="flex flex-col gap-3 sm:flex-row justify-center pt-4">
                    <ButtonLink to="/donate" variant="primary" className="uppercase tracking-wider text-sm !px-6">Try again</ButtonLink>
                    <ButtonLink to="/contact" variant="lime" className="uppercase tracking-wider text-sm !px-6">
                      Contact the organization
                    </ButtonLink>
                  </div>
                </>
              ) : null}
            </div>
          ) : null}
        </div>
      </section>
    </div>
  );
};
